import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { BasicProviderProps, CatalogType, ResourceType, ResourceVersionArtifactType, ResourceVersionType, SoapModuleContextFormType, SoapModuleContextType } from '../types'
import ErrorBoundary from '../components/error/ErrorBoundary'
import { DashboardContext } from './DashboardContext';
import useDataClient from '../hooks/useDataClient';
import { AppContext } from './AppContext';
import { resourcesAdapter } from '../adapter';
import { DocumentationContext } from './DocumentationContext';

export const SoapModuleContext = createContext<SoapModuleContextType | null>(null);

export default function SoapModuleProvider({ children }: BasicProviderProps) {
    const appContext = useContext(AppContext)
    const dashboardContext = useContext(DashboardContext)
    const documentationContext = useContext(DocumentationContext)

    const newResourceClient = useDataClient<number>({ appContext })
    const editResourceClient = useDataClient<number>({ appContext })
    const updateVersionClient = useDataClient<number>({ appContext })
    const deleteResourceClient = useDataClient<number>({ appContext })
    const deleteArtifactClient = useDataClient<number>({ appContext })
    const [resourceTarget, setResourceTarget] = useState<ResourceType | undefined>();
    const soapData = dashboardContext?.appData['soap'];
    const catalogData = dashboardContext?.appData.catalogApp;
    const groupList = useMemo<string[]>(() => catalogData?.GSOAP?.map(c => c.label) || [], [catalogData])
    const [groupSelected, setGroupSelected] = useState<string>(groupList[0])
    const [showForm, setShowForm] = useState<SoapModuleContextFormType>(null)

    const listData = useMemo<ResourceType[]>(() => soapData?.filter(r => r.name.startsWith(groupSelected)) || [], [groupSelected, soapData])

    const availableArtifactOptions = useMemo<CatalogType[]>(() => {
        let currentArtifacts = resourceTarget?.versions[0]?.artifacts.map(a => a.type) || []
        return (dashboardContext?.getCatalog('ART') || []).filter(c => c.active && !currentArtifacts.includes(String(c.value)))
    }, [dashboardContext, resourceTarget])

    const reloadResources = useCallback((message?: string) => {
        dashboardContext?.updateAppData('soap')
        setShowForm(null)
        message && appContext?.setAppDialog({ message: message, type: 'success' })
    }, [])

    function showResourceForm(resource?: ResourceType) {
        setShowForm('Referencia')
        setResourceTarget(resource)
    }

    function showArtifactForm(resource: ResourceType) {
        setShowForm('Artefacto')
        setResourceTarget(resource)
    }

    function addResource(request: ResourceType, files: File[]) {
        newResourceClient.executeRequest(resourcesAdapter.addResource(request, files))
    }

    function editResource(request: ResourceType) {
        editResourceClient.executeRequest(resourcesAdapter.editResource(request))
    }

    function editVersion(version: ResourceVersionType, files: File[]) {
        updateVersionClient.executeRequest(resourcesAdapter.updateVersion(version, files))
    }

    function updateArtifacts(version: ResourceVersionType, files: File[]) {
        updateVersionClient.executeRequest(resourcesAdapter.updateVersion(version, files))
    }

    function confirmResourceDelete(item: ResourceType) {
        appContext?.setAppDialog({
            title: 'Aviso',
            message: `¿Desea suprimir la referencia SOAP ${item.label}?`,
            type: 'warning',
            onAccept: () => deleteResourceClient.executeRequest(resourcesAdapter.deleteResource(item))
        })
    }

    function confirmArtifactDelete(data: ResourceVersionArtifactType) {
        appContext?.setAppDialog({
            title: 'Aviso',
            message: `¿Desea suprimir el artefacto ${data.name}?`,
            type: 'warning',
            onAccept: () => deleteArtifactClient.executeRequest(resourcesAdapter.deleteArtifact(data))
        })
    }

    const soapModuleContext = useMemo<SoapModuleContextType>(() => ({
        listData,
        availableArtifactOptions,
        groupList,
        groupSelected,
        setGroupSelected,
        confirmResourceDelete,
        showForm,
        showResourceForm,
        showArtifactForm,
        closeForm: () => setShowForm(null),
        resourceTarget,
        addResource,
        editResource,
        editVersion,
        updateArtifacts,
        confirmArtifactDelete
    }), [listData, availableArtifactOptions, groupList, groupSelected, showForm])

    useEffect(() => {
        let requestAddedId = newResourceClient.status.data
        if (requestAddedId) {
            reloadResources(`Referencia agregada con id ${requestAddedId}`)
        }
    }, [newResourceClient.status.data])

    useEffect(() => {
        let requestAddedId = editResourceClient.status.data
        if (requestAddedId) {
            reloadResources(`Referencia editada`)
        }
    }, [editResourceClient.status.data])

    useEffect(() => {
        let requestUpdate = updateVersionClient.status.data
        if (requestUpdate) {
            reloadResources(`Artefactos actualizados`)
        }
    }, [updateVersionClient.status.data])

    useEffect(() => {
        if (deleteArtifactClient.status.data) {
            reloadResources('Artefacto eliminado')
        }
    }, [deleteArtifactClient.status.data])

    useEffect(() => {
        if (deleteResourceClient.status.data) {
            reloadResources('Referencia eliminada')
        }
    }, [deleteResourceClient.status.data])

    useEffect(() => {
        if (!showForm) {
            setResourceTarget(undefined)
        }
    }, [showForm])

    useEffect(() => {
        if (documentationContext?.reloadExternalContent) {
            reloadResources()
        }
    }, [documentationContext?.reloadExternalContent])

    return <ErrorBoundary>
        <SoapModuleContext.Provider value={soapModuleContext}>
            {children}
        </SoapModuleContext.Provider>
    </ErrorBoundary>
}
